import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColDef, RowClickedEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { PlusIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { SettingsListDetailLayout } from '@/components/common/SettingsListDetailLayout';
import { Button } from '@/components/ui/button';
import { useGridDeselectOnClose } from '@/hooks/useGridDeselectOnClose';
import type { Location } from '../data/databaseSchema';
import { locationService } from '../services/locationService';
import {
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridSettingsContainerStyles,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { LocationDrawer } from './LocationDrawer';

type LocationPanelMode = 'empty' | 'view' | 'edit' | 'add';

export const OfficeSettings: React.FC = () => {
  const queryClient = useQueryClient();

  const {
    data: locationsData,
    isLoading: isLoadingLocations,
    error: locationsError,
  } = useQuery({
    queryKey: ['locations'],
    queryFn: () => locationService.getLocations(),
    staleTime: 1000 * 60 * 5,
    retry: 1,
  });

  const locations = locationsData || [];

  const [locationPanelMode, setLocationPanelMode] = useState<LocationPanelMode>('empty');
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const locationJustSavedRef = useRef(false);
  const locationGridRef = useRef<AgGridReact>(null);

  const handleAddLocation = () => {
    setEditingLocation(null);
    setLocationPanelMode('add');
  };

  const handleSaveLocation = (_location: Location) => {
    queryClient.invalidateQueries({ queryKey: ['locations'] });
  };

  const handleDeleteLocation = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    try {
      await locationService.deleteLocation(id);
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setSelectedLocation(null);
      setLocationPanelMode('empty');
    } catch (error) {
      toast.error(
        `Failed to delete location: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  const handleLocationRowClicked = useCallback((event: RowClickedEvent<Location>) => {
    if (!event.data) return;
    setSelectedLocation(event.data);
    setLocationPanelMode('view');
    setEditingLocation(null);
  }, []);

  const handleLocationAddClose = useCallback(() => {
    if (locationJustSavedRef.current) {
      locationJustSavedRef.current = false;
      return;
    }
    setLocationPanelMode('empty');
    setEditingLocation(null);
  }, []);

  const handleLocationEditClose = useCallback(() => {
    if (locationJustSavedRef.current) {
      locationJustSavedRef.current = false;
      setEditingLocation(null);
      return;
    }
    setLocationPanelMode('view');
    setEditingLocation(null);
  }, []);

  const handleLocationViewClose = useCallback(() => {
    setLocationPanelMode('empty');
    setSelectedLocation(null);
  }, []);

  const locationColumnDefs: ColDef<Location>[] = useMemo(
    () => [
      { field: 'name', headerName: 'Location Name', flex: 1 },
      {
        field: 'office_head',
        headerName: 'Office Head',
        flex: 1,
        valueFormatter: (p) => p.value || '-',
      },
    ],
    []
  );

  const locationGridOptions = useMemo(
    () => ({
      ...agGridDefaultOptions,
      onRowClicked: handleLocationRowClicked,
      rowSelection: { mode: 'singleRow' as const, checkboxes: false },
    }),
    [handleLocationRowClicked]
  );

  const locationDetailOpen = locationPanelMode !== 'empty';

  useGridDeselectOnClose(locationGridRef, locationDetailOpen);

  return (
    <div className="p-2">
      <style>{agGridThemeStyles}</style>
      <SettingsListDetailLayout
        detailOpen={locationDetailOpen}
        detail={
          locationPanelMode === 'add' ? (
            <LocationDrawer
              isOpen={true}
              onClose={handleLocationAddClose}
              onSave={handleSaveLocation}
              onEntrySaved={() => {
                locationJustSavedRef.current = true;
                queryClient.invalidateQueries({ queryKey: ['locations'] });
                setLocationPanelMode('empty');
                setSelectedLocation(null);
                setEditingLocation(null);
              }}
              location={null}
              isEdit={false}
            />
          ) : locationPanelMode === 'edit' && editingLocation ? (
            <LocationDrawer
              isOpen={true}
              onClose={handleLocationEditClose}
              onSave={(location) => {
                handleSaveLocation(location);
                setSelectedLocation(location);
              }}
              onEntrySaved={() => {
                locationJustSavedRef.current = true;
                queryClient.invalidateQueries({ queryKey: ['locations'] });
                setLocationPanelMode('view');
                setEditingLocation(null);
              }}
              location={editingLocation}
              isEdit
            />
          ) : locationPanelMode === 'view' && selectedLocation ? (
            <LocationDrawer
              key={selectedLocation.id}
              isOpen={true}
              readOnly
              location={selectedLocation}
              onClose={handleLocationViewClose}
              onSave={() => {}}
              onEditRequest={() => {
                setEditingLocation(selectedLocation);
                setLocationPanelMode('edit');
              }}
              onDeleteRequest={() => void handleDeleteLocation(selectedLocation.id)}
            />
          ) : null
        }
        list={
          <>
            <div className="mb-4 flex shrink-0 items-center justify-between">
              <h2 className="m-0 text-2xl font-semibold text-primary">Location</h2>
              <Button type="button" onClick={handleAddLocation}>
                <PlusIcon data-icon="inline-start" />
                Add Location
              </Button>
            </div>

            {isLoadingLocations ? (
              <p className="p-8 text-center text-text-secondary">Loading locations...</p>
            ) : locationsError ? (
              <p className="p-8 text-center text-danger-600">
                Failed to load locations. Please try again.
              </p>
            ) : locations.length === 0 ? (
              <p className="p-8 text-center text-text-secondary">
                No locations found. Add your first location to get started.
              </p>
            ) : (
              <div
                className="ag-theme-material min-h-0 w-full flex-1"
                style={agGridSettingsContainerStyles}
              >
                <AgGridReact
                  ref={locationGridRef}
                  theme={agGridTheme}
                  rowData={locations}
                  columnDefs={locationColumnDefs}
                  defaultColDef={agGridDefaultColDef}
                  gridOptions={locationGridOptions}
                  domLayout="normal"
                  rowNumbers
                  getRowId={(params) => params.data.id}
                />
              </div>
            )}
          </>
        }
      />
    </div>
  );
};
